import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import AccessControlApi from '../api/AccessControlApi'
import { buildPermissionCode, isPartnerUser, matchInterfaceCodeForPath } from '../utils/accessControlUtils'

const AccessControlContext = createContext(null)

const CLIENT_MODULES_KEY = 'clientModules'

const loadStoredPermissions = () => {
  try {
    const raw = localStorage.getItem('permissions')
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const loadStoredClientModules = () => {
  try {
    const raw = localStorage.getItem(CLIENT_MODULES_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

const isSuperAdminUser = () => {
  try {
    const perms = loadStoredPermissions()
    if (perms.__superAdmin === true) return true
    return false
  } catch {
    return false
  }
}

export const AccessControlProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(loadStoredPermissions)
  const [clientModules, setClientModules] = useState(loadStoredClientModules)
  const [loading, setLoading] = useState(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState(isSuperAdminUser)

  const [isPartner, setIsPartner] = useState(isPartnerUser)

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('accessToken')
    if (!token) return

    if (isPartnerUser()) {
      setIsPartner(true)
      setIsSuperAdmin(false)
      return
    }

    // Only block UI when we have no cached permissions — avoids remounting
    // protected pages (e.g. dashboard KPI) right after login.
    const hasCache = Object.keys(loadStoredPermissions()).length > 0 || isSuperAdminUser()
    if (!hasCache) setLoading(true)
    try {
      const response = await AccessControlApi.getMyPermissions()
      const data = response?.data || {}
      const merged = {
        ...data.permissions,
        __superAdmin: data.__superAdmin,
      }
      setPermissions(merged)
      setIsSuperAdmin(!!data.__superAdmin)
      localStorage.setItem('permissions', JSON.stringify(merged))
      if (Array.isArray(data.clientModules)) {
        setClientModules(data.clientModules)
        localStorage.setItem(CLIENT_MODULES_KEY, JSON.stringify(data.clientModules))
      }
    } catch {
      // keep cached permissions on failure
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const isInterfaceBlocked = useCallback(
    (interfaceCode) => {
      if (!clientModules || !interfaceCode) return false
      return clientModules.some(
        (product) =>
          product.enabled === false &&
          (product.interfaceCodes || []).includes(interfaceCode)
      )
    },
    [clientModules]
  )

  const isPathBlocked = useCallback(
    (path) => {
      if (!clientModules || !path) return false
      const normalized = String(path).split('?')[0].split('#')[0]
      let best = null
      for (const product of clientModules) {
        for (const prefix of product.pathPrefixes || []) {
          const matches = normalized === prefix || normalized.startsWith(`${prefix}/`)
          if (!matches) continue
          if (!best || prefix.length > best.prefix.length) {
            best = { enabled: product.enabled !== false, prefix }
          }
        }
      }
      return Boolean(best && best.enabled === false)
    },
    [clientModules]
  )

  const isProductEnabled = useCallback(
    (productKey) => {
      if (!clientModules || !productKey) return true
      const product = clientModules.find((item) => item.key === productKey)
      if (!product) return true
      return product.enabled !== false
    },
    [clientModules]
  )

  const can = useCallback(
    (code) => {
      const interfaceCode = String(code || '').split('.')[0]
      if (isInterfaceBlocked(interfaceCode)) return false
      if (isPartner || isPartnerUser()) return true
      if (isSuperAdmin || permissions.__superAdmin) return true
      if (permissions[code] === true) return true
      if (code.endsWith('.view')) {
        const consultCode = code.replace(/\.view$/, '.consult')
        return permissions[consultCode] === true
      }
      return false
    },
    [permissions, isSuperAdmin, isPartner, isInterfaceBlocked]
  )

  const canAction = useCallback(
    (interfaceCode, actionCode = 'view') => can(buildPermissionCode(interfaceCode, actionCode)),
    [can]
  )

  const canPath = useCallback(
    (path) => {
      if (isPathBlocked(path)) return false
      if (isPartner || isPartnerUser()) return true
      if (isSuperAdmin || permissions.__superAdmin) return true
      const code = matchInterfaceCodeForPath(path)
      if (!code) {
        const normalized = path?.split('?')[0] || ''
        if (
          normalized.startsWith('/gestion-') ||
          normalized.startsWith('/connectivite') ||
          normalized.startsWith('/cartes-acces') ||
          normalized.startsWith('/hotel-')
        ) {
          return false
        }
        return true
      }
      const viewCode = buildPermissionCode(code, 'view')
      if (permissions[viewCode] === true) return true
      const consultCode = buildPermissionCode(code, 'consult')
      if (permissions[consultCode] === true) return true
      // Circuits reservations list: visible with any circuits module access
      if (code === 'circuits_reservations') {
        return (
          permissions['circuits_liste.view'] === true ||
          permissions['circuits_moteur.view'] === true ||
          permissions['circuits_liste.consult'] === true ||
          permissions['circuits_moteur.consult'] === true
        )
      }
      return false
    },
    [isSuperAdmin, permissions, isPartner, isPathBlocked]
  )

  const value = useMemo(
    () => ({
      permissions,
      loading,
      isSuperAdmin: isSuperAdmin || permissions.__superAdmin === true,
      isPartner: isPartner || isPartnerUser(),
      can,
      canAction,
      canPath,
      isModulePathBlocked: isPathBlocked,
      isProductEnabled,
      refresh,
    }),
    [permissions, loading, isSuperAdmin, isPartner, can, canAction, canPath, isPathBlocked, isProductEnabled, refresh]
  )

  return (
    <AccessControlContext.Provider value={value}>{children}</AccessControlContext.Provider>
  )
}

export const useAccessControl = () => {
  const ctx = useContext(AccessControlContext)
  if (!ctx) {
    throw new Error('useAccessControl must be used within AccessControlProvider')
  }
  return ctx
}

/** Non-throwing variant — returns null when no AccessControlProvider is mounted. */
export const useAccessControlOptional = () => useContext(AccessControlContext)

export default AccessControlContext
